import { Injectable, inject } from "@angular/core";
import {
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from "@angular/common/http";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslocoService } from "@jsverse/transloco";
import { environment } from "src/environments/environment";

/**
 * Set to `true` on a request's `context` to keep it quiet — for writes the user didn't trigger as a
 * save (background sync, autosave) or for callers that already announce their own result.
 */
export const SKIP_SAVE_FEEDBACK = new HttpContextToken<boolean>(() => false);

/**
 * App-API path segments whose POST/PUT/PATCH is an action, an upload or a background write rather
 * than "add" or "update" of a record. Matched against the first path segment after the API root.
 */
const QUIET_RESOURCES = new Set<string>([
  "authentication",
  "files",
  "otp",
  "me",
  "grades",
  "watch_times",
  "payments_status",
  "users_setpassword",
  "users_sendinvite",
  "grades_sendcertificate",
  "mail",
  "mail_verify",
  "mail_senders_verify",
  "meet",
  "meet_now",
  "auto-grade",
  "facebook_leads",
  "inbound-messages",
  "referral_code",
  "registrations",
  "onboardings",
  "user_settings",
  "diagnostics"
]);

/** Last path segments that mark a POST as an action rather than a create. */
const QUIET_ACTIONS = new Set<string>(["generate", "reorder", "invite", "share", "reply", "verify"]);

/**
 * Shows "Added successfully" after a POST and "Updated successfully" after a PUT/PATCH to the app
 * API. Errors are left to `ErrorInterceptor`; only the final 2xx response is announced.
 */
@Injectable()
export class SaveFeedbackInterceptor implements HttpInterceptor {
  private readonly snackBar = inject(MatSnackBar);
  private readonly transloco = inject(TranslocoService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const key = this.messageKey(request);
    if (key === null) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && event.ok) {
          this.snackBar.open(this.transloco.translate(key), this.transloco.translate("common.close"), {
            duration: 3000,
            verticalPosition: "top"
          });
        }
      })
    );
  }

  private messageKey(request: HttpRequest<unknown>): string | null {
    if (request.context.get(SKIP_SAVE_FEEDBACK) || !request.url.startsWith(environment.apiUrl)) {
      return null;
    }

    const method = request.method.toUpperCase();
    if (method !== "POST" && method !== "PUT" && method !== "PATCH") {
      return null;
    }

    const segments = request.url
      .slice(environment.apiUrl.length)
      .split("?")[0]
      .split("/")
      .filter((s) => s.length > 0);
    if (segments.length === 0 || QUIET_RESOURCES.has(segments[0])) {
      return null;
    }
    if (method === "POST" && QUIET_ACTIONS.has(segments[segments.length - 1])) {
      return null;
    }

    return method === "POST" ? "common.addedSuccessfully" : "common.updatedSuccessfully";
  }
}
